/**
 * Post-Add-to-Cart Modal Web Component
 *
 * Shows cross-sell recommendations after a product is added to cart.
 * Uses pre-embedded JSON product data (zero API calls on open).
 * GA4 tracking: post_atc_modal_view, post_atc_modal_addon, post_atc_modal_dismiss
 */
if (!customElements.get('post-atc-modal')) {
  customElements.define(
    'post-atc-modal',
    class PostAtcModal extends HTMLElement {
      constructor() {
        super();

        this.productData = null;
        this.touchStartY = 0;
        this.addedItems = new Set();
        this.previousFocus = null;

        // Bind handlers
        this.onBackdropClick = this.onBackdropClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);

        // Wire up static buttons
        var close = this.querySelector('.post-atc-modal__close');
        if (close) close.addEventListener('click', () => this.hide('dismiss'));

        var cartBtn = this.querySelector('.post-atc-modal__cart-btn');
        if (cartBtn) cartBtn.addEventListener('click', () => this.goToCart());

        var keepBtn = this.querySelector('.post-atc-modal__keep-btn');
        if (keepBtn) keepBtn.addEventListener('click', () => this.hide('keep_shopping'));
      }

      connectedCallback() {
        this.loadProductData();
      }

      loadProductData() {
        this.productData = {};

        var mainEl = document.getElementById('post-atc-product-data');
        if (mainEl) {
          try {
            Object.assign(this.productData, JSON.parse(mainEl.textContent));
          } catch (e) { /* empty */ }
        }

        var extEl = document.getElementById('post-atc-product-data-ext');
        if (extEl) {
          try {
            var ext = JSON.parse(extEl.textContent);
            delete ext._loaded;
            Object.assign(this.productData, ext);
          } catch (e) { /* empty */ }
        }
      }

      /**
       * Show the modal with recommendations for the added product.
       */
      show(opts) {
        if (!this.productData) this.loadProductData();

        // Save focus for restore on close
        this.previousFocus = document.activeElement;

        var title = opts.productTitle || 'Item';
        var variant = opts.variantTitle ? ' (' + opts.variantTitle + ')' : '';

        var addedText = this.querySelector('.post-atc-modal__added-text');
        if (addedText) addedText.textContent = title + variant + ' added to cart';

        var recs = this.getRecommendations(opts.productHandle || '');
        var heading = this.querySelector('.post-atc-modal__heading');
        if (heading) heading.textContent = recs.heading;

        this.renderRecommendations(recs.items);

        this.setAttribute('aria-hidden', 'false');
        document.body.classList.add('overflow-hidden');
        this.addedItems.clear();

        // Attach listeners
        var backdrop = this.querySelector('.post-atc-modal__backdrop');
        if (backdrop) backdrop.addEventListener('click', this.onBackdropClick);
        document.addEventListener('keydown', this.onKeyDown);

        // Mobile swipe-down
        var sheet = this.querySelector('.post-atc-modal__sheet');
        if (sheet) {
          sheet.addEventListener('touchstart', this.onTouchStart, { passive: true });
          sheet.addEventListener('touchend', this.onTouchEnd, { passive: true });
        }

        // Focus the close button for keyboard accessibility
        var closeBtn = this.querySelector('.post-atc-modal__close');
        if (closeBtn) closeBtn.focus();

        this.trackEvent('post_atc_modal_view', { product_handle: opts.productHandle });
      }

      hide(reason) {
        this.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('overflow-hidden');

        var backdrop = this.querySelector('.post-atc-modal__backdrop');
        if (backdrop) backdrop.removeEventListener('click', this.onBackdropClick);
        document.removeEventListener('keydown', this.onKeyDown);

        var sheet = this.querySelector('.post-atc-modal__sheet');
        if (sheet) {
          sheet.removeEventListener('touchstart', this.onTouchStart);
          sheet.removeEventListener('touchend', this.onTouchEnd);
        }

        // Restore previous focus
        if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
          this.previousFocus.focus();
          this.previousFocus = null;
        }

        if (reason === 'dismiss' || reason === 'keep_shopping') {
          this.trackEvent('post_atc_modal_dismiss', {
            dismiss_method: reason,
            addons_count: this.addedItems.size
          });
        }
      }

      goToCart() {
        this.hide('cart');

        var cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          // Paint drawer immediately, refresh in background.
          cartDrawer.open();
          if (typeof cartDrawer.refreshHTML === 'function') cartDrawer.refreshHTML();
        } else {
          window.location = window.routes ? window.routes.cart_url : '/cart';
        }
      }

      onBackdropClick() {
        this.hide('dismiss');
      }

      onKeyDown(e) {
        if (e.key === 'Escape') {
          this.hide('dismiss');
          return;
        }

        // Focus trap: keep Tab within the modal
        if (e.key === 'Tab') {
          var focusable = this.querySelectorAll(
            'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
          if (focusable.length === 0) return;

          var first = focusable[0];
          var last = focusable[focusable.length - 1];

          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }

      onTouchStart(e) {
        this.touchStartY = e.touches[0].clientY;
      }

      onTouchEnd(e) {
        var deltaY = e.changedTouches[0].clientY - this.touchStartY;
        if (deltaY > 80) this.hide('swipe');
      }

      getRecommendations(handle) {
        var map = this.getRecommendationMap();
        var entry = map[handle];

        if (!entry) {
          var keys = Object.keys(map);
          for (var i = 0; i < keys.length; i++) {
            if (keys[i] !== '_default' && handle.indexOf(keys[i]) === 0) {
              entry = map[keys[i]];
              break;
            }
          }
        }

        if (!entry) entry = map._default;

        var items = [];
        for (var j = 0; j < entry.recs.length; j++) {
          var rec = entry.recs[j];
          var product = this.productData[rec.key];
          if (product && product.available) {
            items.push({
              key: rec.key,
              title: product.title,
              url: product.url,
              price: product.price_formatted,
              image: product.image,
              variantId: product.variant_id,
              copy: rec.copy
            });
          }
        }

        // Fill from defaults if too few
        if (items.length < 2) {
          var defaults = map._default.recs;
          for (var k = 0; k < defaults.length && items.length < 3; k++) {
            var dp = this.productData[defaults[k].key];
            if (dp && dp.available && !items.some(function(item) { return item.key === defaults[k].key; })) {
              items.push({
                key: defaults[k].key,
                title: dp.title,
                url: dp.url,
                price: dp.price_formatted,
                image: dp.image,
                variantId: dp.variant_id,
                copy: defaults[k].copy
              });
            }
          }
        }

        return {
          heading: entry ? entry.heading : 'You might also like:',
          items: items.slice(0, 3)
        };
      }

      getRecommendationMap() {
        return {
          'helios-long-sleeve-sun-shirt': {
            heading: 'Complete your sun protection kit:',
            recs: [
              { key: 'sun-hat', copy: 'Stays on all day on the water' },
              { key: 'windrider-3-4-sun-gloves', copy: 'The last 2 inches most anglers miss' },
              { key: 'hooded-helios-with-gaiter', copy: 'More coverage, hood included' }
            ]
          },
          'hooded-helios-with-gaiter': {
            heading: 'Complete your sun protection kit:',
            recs: [
              { key: 'sun-hat', copy: 'Extra brim when the hood isn\'t enough' },
              { key: 'windrider-3-4-sun-gloves', copy: 'Hands are the first to burn' },
              { key: 'atoll-hooded-shirt-with-gaiter-1', copy: 'Thumbholes + back pocket for different days' }
            ]
          },
          'atoll-hooded-shirt-with-gaiter-1': {
            heading: 'Complete your sun protection kit:',
            recs: [
              { key: 'sun-hat', copy: 'Wide brim pairs perfectly' },
              { key: 'windrider-3-4-sun-gloves', copy: 'Match your hand protection' },
              { key: 'helios-long-sleeve-sun-shirt', copy: 'Pick a different color for day 2' }
            ]
          },
          'pro-all-weather-rain-gear-set': {
            heading: 'Complete your rain gear setup:',
            recs: [
              { key: 'winter-gloves', copy: 'Keep your hands dry when the rain hits' },
              { key: 'tempest-fishing-bibs', copy: 'Premium fishing-specific bibs' },
              { key: 'hayward-suit', copy: 'If you fish open water too' }
            ]
          },
          'pro-all-weather-rain-jacket': {
            heading: 'Complete the suit:',
            recs: [
              { key: 'pro-all-weather-rain-bibs', copy: 'Bibs are the other half of the system' },
              { key: 'tempest-fishing-bibs', copy: 'Premium fishing-specific bibs' },
              { key: 'pro-all-weather-rain-gear-set', copy: 'Save vs buying separately' }
            ]
          },
          'pro-all-weather-rain-bibs': {
            heading: 'Complete the suit:',
            recs: [
              { key: 'pro-all-weather-rain-jacket', copy: 'Everything you need, nothing you don\'t' },
              { key: 'winter-gloves', copy: 'Rain + cold hands = miserable day' },
              { key: 'pro-all-weather-rain-gear-set', copy: 'Save and get both in one' }
            ]
          },
          'tempest-fishing-bibs': {
            heading: 'Complete your rain gear setup:',
            recs: [
              { key: 'pro-all-weather-rain-jacket', copy: 'The jacket that pairs with every bib' },
              { key: 'winter-gloves', copy: 'Your hands work harder in the rain' },
              { key: 'hayward-suit', copy: 'Same protection, 3-season use' }
            ]
          },
          'hayward-3-season-float-set-rain-cold-weather-gear': {
            heading: 'Pair with your float suit:',
            recs: [
              { key: 'hayward-fliptop-mittens', copy: 'Convertible for fish handling' },
              { key: 'winter-gloves', copy: 'Stay dry everywhere the suit ends' },
              { key: 'hayward-3-season-float-jacket', copy: 'Second colorway or gift option' }
            ]
          },
          'hayward-3-season-float-jacket': {
            heading: 'Complete your Hayward kit:',
            recs: [
              { key: 'hayward-waterproof-float-bibs', copy: 'Complete the suit — both pieces together' },
              { key: 'hayward-fliptop-mittens', copy: 'Convertible gloves for boat anglers' },
              { key: 'hayward-suit', copy: 'Save vs buying both pieces separately' }
            ]
          },
          'hayward-waterproof-float-bibs': {
            heading: 'Complete your Hayward kit:',
            recs: [
              { key: 'hayward-3-season-float-jacket', copy: 'Complete the suit' },
              { key: 'hayward-fliptop-mittens', copy: 'Convertible gloves for boat anglers' },
              { key: 'hayward-suit', copy: 'Save vs buying both pieces separately' }
            ]
          },
          'windrider-ice-suit': {
            heading: 'Complete your ice fishing kit:',
            recs: [
              { key: 'hayward-fliptop-mittens', copy: 'For rigging holes with cold hands' },
              { key: 'winter-gloves', copy: 'Protection from wrist to fingertip' },
              { key: 'boreas-suit', copy: 'Buy for a buddy — most guys fish with someone' }
            ]
          },
          'boreas_ice_fishing_suit': {
            heading: 'Complete your ice fishing kit:',
            recs: [
              { key: 'hayward-fliptop-mittens', copy: 'Essential for baiting hooks in -20\u00b0F' },
              { key: 'winter-gloves', copy: 'Match your hand protection to your suit' },
              { key: 'boreas-pro-floating-ice-fishing-bibs', copy: 'Separates for mix-and-match sizing' }
            ]
          },
          'boreas-pro-floating-ice-fishing-bibs': {
            heading: 'Complete your Boreas kit:',
            recs: [
              { key: 'hayward-fliptop-mittens', copy: 'Your hands need protection too' },
              { key: 'boreas-pro-floating-ice-fishing-jacket', copy: 'Complete the suit — buy the other half' },
              { key: 'boreas-suit', copy: 'Save vs buying both pieces separately' }
            ]
          },
          'boreas-pro-floating-ice-fishing-jacket': {
            heading: 'Complete your Boreas kit:',
            recs: [
              { key: 'hayward-fliptop-mittens', copy: 'Your hands need protection too' },
              { key: 'boreas-pro-floating-ice-fishing-bibs', copy: 'Complete the suit — buy the other half' },
              { key: 'boreas-suit', copy: 'Save vs buying both pieces separately' }
            ]
          },
          'womens-ice-fishing-suit': {
            heading: 'Complete your ice fishing kit:',
            recs: [
              { key: 'hayward-fliptop-mittens', copy: 'Convertible for fish handling in the cold' },
              { key: 'womens-ice-bibs', copy: 'Complete your Women\'s Ice kit' },
              { key: 'womens-ice-jacket', copy: 'Mix-and-match sizing option' }
            ]
          },
          'womens-ice-jacket': {
            heading: 'Complete your ice fishing kit:',
            recs: [
              { key: 'hayward-fliptop-mittens', copy: 'Convertible for fish handling' },
              { key: 'womens-ice-bibs', copy: 'Complete your Women\'s Ice kit' },
              { key: 'womens-ice-fishing-suit', copy: 'Save with the full suit' }
            ]
          },
          'womens-ice-bibs': {
            heading: 'Complete your ice fishing kit:',
            recs: [
              { key: 'hayward-fliptop-mittens', copy: 'Convertible for fish handling' },
              { key: 'womens-ice-jacket', copy: 'Complete your Women\'s Ice kit' },
              { key: 'womens-ice-fishing-suit', copy: 'Save with the full suit' }
            ]
          },
          'womens-helios\u2122-hooded-sun-shirt': {
            heading: 'Complete your sun protection kit:',
            recs: [
              { key: 'sun-hat', copy: 'Extra brim coverage for sun protection' },
              { key: 'windrider-3-4-sun-gloves', copy: 'Hands are the first to burn' },
              { key: 'womens-pro-all-weather-bibs', copy: '3-season versatility beyond sun' }
            ]
          },
          'helios\u2122-kids-sun-shirt': {
            heading: 'Great add-ons for the family:',
            recs: [
              { key: 'sun-hat', copy: 'Wide brim sun protection for the family' },
              { key: 'helios-long-sleeve-sun-shirt', copy: 'Match your kid — grab one for yourself' },
              { key: 'womens-helios', copy: 'Sun protection for mom too' }
            ]
          },
          _default: {
            heading: 'You might also like:',
            recs: [
              { key: 'sun-hat', copy: 'Our most popular accessory' },
              { key: 'windrider-3-4-sun-gloves', copy: 'UPF 50+ hand protection' },
              { key: 'helios-long-sleeve-sun-shirt', copy: 'UPF 50+ sun protection shirt' }
            ]
          }
        };
      }

      renderRecommendations(items) {
        var container = this.querySelector('.post-atc-modal__recommendations');
        if (!container) return;

        container.innerHTML = '';

        for (var i = 0; i < items.length; i++) {
          var item = items[i];
          var card = document.createElement('div');
          card.className = 'post-atc-modal__rec';

          var img = document.createElement('img');
          img.className = 'post-atc-modal__rec-image';
          img.src = item.image;
          img.alt = item.title;
          img.width = 64;
          img.height = 64;
          img.loading = 'lazy';
          card.appendChild(img);

          var info = document.createElement('div');
          info.className = 'post-atc-modal__rec-info';

          var nameLink = document.createElement('a');
          nameLink.href = item.url;
          nameLink.className = 'post-atc-modal__rec-name';
          nameLink.textContent = item.title;
          info.appendChild(nameLink);

          var copyP = document.createElement('p');
          copyP.className = 'post-atc-modal__rec-copy';
          copyP.textContent = item.copy;
          info.appendChild(copyP);

          var priceSpan = document.createElement('span');
          priceSpan.className = 'post-atc-modal__rec-price';
          priceSpan.textContent = item.price;
          info.appendChild(priceSpan);

          card.appendChild(info);

          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'post-atc-modal__add-btn';
          btn.dataset.variantId = String(item.variantId);
          btn.dataset.productKey = item.key;
          btn.textContent = '+ Add';
          btn.addEventListener('click', this.handleAddClick.bind(this));
          card.appendChild(btn);

          container.appendChild(card);
        }
      }

      async handleAddClick(e) {
        var btn = e.currentTarget;
        var variantId = btn.dataset.variantId;
        var productKey = btn.dataset.productKey;

        if (btn.disabled) return;
        btn.disabled = true;
        btn.textContent = 'Adding...';

        try {
          var response = await fetch(window.routes ? window.routes.cart_add_url : '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
          });

          if (!response.ok) throw new Error('Failed to add');

          btn.textContent = '\u2713 Added';
          btn.classList.add('post-atc-modal__add-btn--added');
          this.addedItems.add(productKey);

          this.trackEvent('post_atc_modal_addon', {
            product_key: productKey,
            variant_id: variantId
          });

          // Publish cart update for header badge
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'post-atc-modal' });
          }
        } catch (err) {
          btn.textContent = 'Error \u2014 retry';
          btn.disabled = false;
        }
      }

      trackEvent(eventName, params) {
        // Manual gtag removed — GA4 is now handled by the Google & YouTube app.
        // if (typeof gtag === 'function') {
        //   gtag('event', eventName, params || {});
        // }
      }
    }
  );
}
